import React, { useEffect, useState } from "react";
import { Moon, Save, Shield, Sun, UserCircle2 } from "lucide-react";
import { useAdminPortal } from "../AdminPortalContext";
import {
  fetchAdminHomeAntiBubbleSettings,
  updateAdminHomeAntiBubbleSettings,
} from "../../../../../utils/homeContentApi";

const themeOptions = [
  {
    value: "light",
    title: "Light Theme",
    description:
      "Clean, high-contrast default workspace for day-to-day admin operations.",
    icon: Sun,
  },
  {
    value: "dark",
    title: "Dark Theme",
    description: "Optional darker surface palette for low-light work sessions.",
    icon: Moon,
  },
];

const defaultAntiBubbleForm = {
  eyebrow: "Why Trust BusiGrow",
  headingPrefix: "We Don't Follow the",
  headingHighlight: "AI Bubble",
  headingSuffix: "We Burst It.",
  description:
    "While others chase trends, we analyze, strategize, and build solutions rooted in real understanding. Not hype. Not templates. Pure, purpose-driven growth.",
  taglineTop: "We're outside the bubble.",
  taglineBottom: "We pop it.",
  bubbleLabels: "GPT, AI, Hype, LLM, Bot, Buzz, Trend, Auto, ML, Gen, Prompt, Token",
  burstIntervalMs: 3200,
  burstDurationMs: 900,
};

const AdminSettingsPage = () => {
  const { adminProfile, setTheme, theme, token, setErrorMessage } =
    useAdminPortal();
  const isDarkTheme = theme === "dark";
  const [antiBubbleForm, setAntiBubbleForm] = useState(defaultAntiBubbleForm);
  const [isSavingAntiBubble, setIsSavingAntiBubble] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadSettings = async () => {
      try {
        const settings = await fetchAdminHomeAntiBubbleSettings(token);

        if (!isMounted || !settings) {
          return;
        }

        setAntiBubbleForm({
          eyebrow: settings.eyebrow || defaultAntiBubbleForm.eyebrow,
          headingPrefix:
            settings.headingPrefix || defaultAntiBubbleForm.headingPrefix,
          headingHighlight:
            settings.headingHighlight || defaultAntiBubbleForm.headingHighlight,
          headingSuffix:
            settings.headingSuffix || defaultAntiBubbleForm.headingSuffix,
          description: settings.description || defaultAntiBubbleForm.description,
          taglineTop: settings.taglineTop || defaultAntiBubbleForm.taglineTop,
          taglineBottom:
            settings.taglineBottom || defaultAntiBubbleForm.taglineBottom,
          bubbleLabels: Array.isArray(settings.bubbleLabels)
            ? settings.bubbleLabels.join(", ")
            : defaultAntiBubbleForm.bubbleLabels,
          burstIntervalMs:
            Number(settings.burstIntervalMs) ||
            defaultAntiBubbleForm.burstIntervalMs,
          burstDurationMs:
            Number(settings.burstDurationMs) ||
            defaultAntiBubbleForm.burstDurationMs,
        });
      } catch (error) {
        setErrorMessage(error.message || "Failed to load anti-bubble settings");
      }
    };

    if (token) {
      loadSettings();
    }

    return () => {
      isMounted = false;
    };
  }, [setErrorMessage, token]);

  const handleAntiBubbleSave = async (event) => {
    event.preventDefault();
    setIsSavingAntiBubble(true);
    setErrorMessage("");

    try {
      const parsedLabels = antiBubbleForm.bubbleLabels
        .split(",")
        .map((label) => label.trim())
        .filter(Boolean);

      await updateAdminHomeAntiBubbleSettings(token, {
        eyebrow: antiBubbleForm.eyebrow,
        headingPrefix: antiBubbleForm.headingPrefix,
        headingHighlight: antiBubbleForm.headingHighlight,
        headingSuffix: antiBubbleForm.headingSuffix,
        description: antiBubbleForm.description,
        taglineTop: antiBubbleForm.taglineTop,
        taglineBottom: antiBubbleForm.taglineBottom,
        bubbleLabels: parsedLabels,
        burstIntervalMs: Number(antiBubbleForm.burstIntervalMs),
        burstDurationMs: Number(antiBubbleForm.burstDurationMs),
      });
    } catch (error) {
      setErrorMessage(error.message || "Failed to save anti-bubble settings");
    } finally {
      setIsSavingAntiBubble(false);
    }
  };

  return (
    <div className="space-y-6">
      <section
        className={`rounded-[28px] border px-6 py-6 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
      >
        <h2 className="text-2xl font-bold">Settings</h2>
        <p
          className={`mt-3 max-w-3xl text-sm leading-7 ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
        >
          Theme and account presentation are ready now. Additional settings such
          as roles, audit logs, notifications, and admin management can be
          integrated into this page later.
        </p>
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <article
          className={`rounded-[28px] border p-6 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
        >
          <h3 className="text-lg font-bold">Appearance</h3>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            {themeOptions.map((option) => {
              const Icon = option.icon;
              const isActive = theme === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setTheme(option.value)}
                  className={`rounded-[24px] border p-5 text-left transition ${isActive ? (isDarkTheme ? "border-sky-500 bg-sky-500/10" : "border-sky-300 bg-sky-50") : isDarkTheme ? "border-slate-800 bg-slate-950 hover:border-slate-700" : "border-slate-200 bg-slate-50 hover:border-slate-300"}`}
                >
                  <div
                    className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${isActive ? "bg-slate-900 text-white" : isDarkTheme ? "bg-slate-800 text-slate-100" : "bg-white text-slate-700"}`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <h4 className="mt-4 text-base font-bold">{option.title}</h4>
                  <p
                    className={`mt-2 text-sm leading-6 ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
                  >
                    {option.description}
                  </p>
                </button>
              );
            })}
          </div>
        </article>

        <div className="space-y-6">
          <article
            className={`rounded-[28px] border p-6 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-2xl ${isDarkTheme ? "bg-slate-950 text-slate-100" : "bg-slate-100 text-slate-700"}`}
              >
                <UserCircle2 className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Admin Profile</h3>
                <p
                  className={`text-sm ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
                >
                  Current signed-in account
                </p>
              </div>
            </div>

            <div
              className={`mt-5 rounded-2xl px-4 py-4 ${isDarkTheme ? "bg-slate-950" : "bg-slate-50"}`}
            >
              <p
                className={`text-xs font-semibold uppercase tracking-[0.22em] ${isDarkTheme ? "text-slate-500" : "text-slate-400"}`}
              >
                Email
              </p>
              <p className="mt-2 text-base font-semibold">
                {adminProfile?.email || "Admin"}
              </p>
            </div>
          </article>

          <article
            className={`rounded-[28px] border p-6 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-2xl ${isDarkTheme ? "bg-slate-950 text-slate-100" : "bg-slate-100 text-slate-700"}`}
              >
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Security Placeholder</h3>
                <p
                  className={`text-sm ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
                >
                  Ready for future configuration
                </p>
              </div>
            </div>

            <ul
              className={`mt-5 space-y-3 text-sm ${isDarkTheme ? "text-slate-300" : "text-slate-600"}`}
            >
              <li>Rotate admin credentials from backend environment</li>
              <li>Add multi-admin support later</li>
              <li>Add role permissions and audit logs later</li>
            </ul>
          </article>
        </div>
      </section>

      <section
        className={`rounded-[28px] border p-6 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
      >
        <h3 className="text-lg font-bold">Home AI Bubble Section</h3>
        <p
          className={`mt-2 text-sm ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
        >
          Controls the "outside the AI bubble, burst it" section on the landing
          page, including text, floating labels, and burst timing.
        </p>

        <form className="mt-5 space-y-4" onSubmit={handleAntiBubbleSave}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="space-y-2">
              <span className="text-sm font-semibold">Eyebrow</span>
              <input
                value={antiBubbleForm.eyebrow}
                onChange={(event) =>
                  setAntiBubbleForm((prev) => ({
                    ...prev,
                    eyebrow: event.target.value,
                  }))
                }
                className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
              />
            </label>
            <label className="space-y-2">
              <span className="text-sm font-semibold">Heading Prefix</span>
              <input
                value={antiBubbleForm.headingPrefix}
                onChange={(event) =>
                  setAntiBubbleForm((prev) => ({
                    ...prev,
                    headingPrefix: event.target.value,
                  }))
                }
                className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
              />
            </label>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="space-y-2">
              <span className="text-sm font-semibold">Heading Highlight</span>
              <input
                value={antiBubbleForm.headingHighlight}
                onChange={(event) =>
                  setAntiBubbleForm((prev) => ({
                    ...prev,
                    headingHighlight: event.target.value,
                  }))
                }
                className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
              />
            </label>
            <label className="space-y-2">
              <span className="text-sm font-semibold">Heading Suffix</span>
              <input
                value={antiBubbleForm.headingSuffix}
                onChange={(event) =>
                  setAntiBubbleForm((prev) => ({
                    ...prev,
                    headingSuffix: event.target.value,
                  }))
                }
                className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
              />
            </label>
          </div>

          <label className="space-y-2 block">
            <span className="text-sm font-semibold">Description</span>
            <textarea
              rows={3}
              value={antiBubbleForm.description}
              onChange={(event) =>
                setAntiBubbleForm((prev) => ({
                  ...prev,
                  description: event.target.value,
                }))
              }
              className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
            />
          </label>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="space-y-2">
              <span className="text-sm font-semibold">Tagline Top</span>
              <input
                value={antiBubbleForm.taglineTop}
                onChange={(event) =>
                  setAntiBubbleForm((prev) => ({
                    ...prev,
                    taglineTop: event.target.value,
                  }))
                }
                className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
              />
            </label>
            <label className="space-y-2">
              <span className="text-sm font-semibold">Tagline Bottom</span>
              <input
                value={antiBubbleForm.taglineBottom}
                onChange={(event) =>
                  setAntiBubbleForm((prev) => ({
                    ...prev,
                    taglineBottom: event.target.value,
                  }))
                }
                className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
              />
            </label>
          </div>

          <label className="space-y-2 block">
            <span className="text-sm font-semibold">Bubble Labels (comma-separated)</span>
            <input
              value={antiBubbleForm.bubbleLabels}
              onChange={(event) =>
                setAntiBubbleForm((prev) => ({
                  ...prev,
                  bubbleLabels: event.target.value,
                }))
              }
              className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
            />
          </label>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="space-y-2">
              <span className="text-sm font-semibold">Burst Interval (ms)</span>
              <input
                type="number"
                min={1200}
                max={10000}
                value={antiBubbleForm.burstIntervalMs}
                onChange={(event) =>
                  setAntiBubbleForm((prev) => ({
                    ...prev,
                    burstIntervalMs: Number(event.target.value) || 1200,
                  }))
                }
                className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
              />
            </label>
            <label className="space-y-2">
              <span className="text-sm font-semibold">Burst Duration (ms)</span>
              <input
                type="number"
                min={300}
                max={3000}
                value={antiBubbleForm.burstDurationMs}
                onChange={(event) =>
                  setAntiBubbleForm((prev) => ({
                    ...prev,
                    burstDurationMs: Number(event.target.value) || 300,
                  }))
                }
                className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${isDarkTheme ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={isSavingAntiBubble}
            className={`inline-flex items-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold transition ${isDarkTheme ? "bg-sky-500 text-slate-950 hover:bg-sky-400" : "bg-slate-900 text-white hover:bg-slate-800"} ${isSavingAntiBubble ? "opacity-70" : ""}`}
          >
            <Save className="h-4 w-4" />
            {isSavingAntiBubble ? "Saving..." : "Save Home Bubble Settings"}
          </button>
        </form>
      </section>
    </div>
  );
};

export default AdminSettingsPage;
